import React, { useCallback, useEffect, useState } from 'react';
import { FiSave, FiRefreshCw, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import Loader from '../../components/common/Loader';

const OPERATIONS = [
  { key: 'create-voucher', label: 'Create Voucher', group: 'Vouchers' },
  { key: 'edit-voucher', label: 'Edit Voucher', group: 'Vouchers' },
  { key: 'delete-voucher', label: 'Delete Voucher', group: 'Vouchers' },
  { key: 'view-voucher', label: 'View Voucher', group: 'Vouchers' },
  { key: 'view-accounts', label: 'View Accounts', group: 'Chart of Accounts' },
  { key: 'view-ledger', label: 'View Ledger', group: 'Ledger' },
  { key: 'view-reports', label: 'View Reports', group: 'Reports' },
];

function AccountTree({ nodes, allowedIds, onToggle, expanded, onToggleExpand, depth = 0 }) {
  const collectAccountIds = (node) => {
    const ids = [];
    if (node.node_type === 'account') ids.push(node.id);
    if (node.children) node.children.forEach((c) => ids.push(...collectAccountIds(c)));
    return ids;
  };

  const isCategoryAllChecked = (node) => {
    const ids = collectAccountIds(node);
    return ids.length > 0 && ids.every((id) => allowedIds.includes(id));
  };

  const isCategorySomeChecked = (node) => {
    const ids = collectAccountIds(node);
    return ids.some((id) => allowedIds.includes(id)) && !isCategoryAllChecked(node);
  };

  return (
    <div>
      {nodes.map((node) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expanded[node.id];
        const isAllowed = allowedIds.includes(node.id);
        const isAccount = node.node_type === 'account';
        const isCategory = node.node_type === 'category';

        return (
          <div key={node.id}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px',
                paddingLeft: depth * 20 + 8,
                fontSize: 13,
                cursor: hasChildren ? 'pointer' : 'default',
                borderRadius: 6,
                background: isAllowed && isAccount ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                fontWeight: node.node_type === 'type' ? 600 : hasChildren ? 500 : 400,
              }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); onToggleExpand(node.id); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, color: 'var(--text-muted)', width: 14 }}
                >
                  {isExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                </button>
              ) : (
                <span style={{ width: 14 }} />
              )}
              {isAccount ? (
                <input
                  type="checkbox"
                  checked={isAllowed}
                  onChange={() => onToggle(node.id)}
                  style={{ width: 16, height: 16 }}
                />
              ) : isCategory ? (
                <input
                  type="checkbox"
                  checked={isCategoryAllChecked(node)}
                  ref={(el) => { if (el) el.indeterminate = isCategorySomeChecked(node); }}
                  onChange={() => {
                    const ids = collectAccountIds(node);
                    const allChecked = ids.every((id) => allowedIds.includes(id));
                    onToggle(ids, allChecked);
                  }}
                  style={{ width: 16, height: 16 }}
                />
              ) : (
                <span style={{ width: 16, height: 16, display: 'inline-block' }} />
              )}
              <span style={{ color: hasChildren ? 'var(--text)' : 'var(--text-secondary)' }}>
                {node.name}
              </span>
              {node.account_nature && isAccount && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  ({node.account_nature})
                </span>
              )}
              {hasChildren && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                  {node.children.length} items
                </span>
              )}
            </label>
            {hasChildren && isExpanded && (
              <AccountTree
                nodes={node.children}
                allowedIds={allowedIds}
                onToggle={onToggle}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AccountPermissions() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [activeOp, setActiveOp] = useState('create-voucher');
  const [allowedIds, setAllowedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [allPermissions, setAllPermissions] = useState({});

  useEffect(() => {
    api.roles.list({ per_page: 100 }).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.roles ? (data.roles.data || data.roles) : data.data || [];
      setRoles(list.filter((r) => String(r.name || '').toLowerCase() !== 'admin'));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedRole) return;
    setLoading(true);
    Promise.all([
      api.accountPermissions.list({ role_id: selectedRole }),
      api.accountPermissions.accounts({ role_id: selectedRole, operation: activeOp }),
    ]).then(([permRes, accRes]) => {
      setAllPermissions(permRes.data.permissions || {});
      setAccounts(accRes.data.accounts || []);
      setAllowedIds(accRes.data.allowed_ids || []);
    }).catch((err) => showError('Failed to load data', extractError(err)))
      .finally(() => setLoading(false));
  }, [selectedRole, activeOp]);

  const toggleAccount = (idOrIds, forceRemove = false) => {
    if (Array.isArray(idOrIds)) {
      if (forceRemove) {
        setAllowedIds((prev) => prev.filter((x) => !idOrIds.includes(x)));
      } else {
        setAllowedIds((prev) => [...new Set([...prev, ...idOrIds])]);
      }
    } else {
      setAllowedIds((prev) => prev.includes(idOrIds) ? prev.filter((x) => x !== idOrIds) : [...prev, idOrIds]);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const map = {};
    const walk = (nodes) => {
      nodes.forEach((n) => {
        if (n.children && n.children.length > 0) {
          map[n.id] = true;
          walk(n.children);
        }
      });
    };
    walk(accounts);
    setExpanded(map);
  };

  const collapseAll = () => setExpanded({});

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.accountPermissions.save({
        role_id: selectedRole,
        operation: activeOp,
        account_ids: allowedIds,
      });
      toast('success', '', 'Account permissions saved');
      // Refresh allPermissions
      const { data } = await api.accountPermissions.list({ role_id: selectedRole });
      setAllPermissions(data.permissions || {});
    } catch (err) {
      showError('Failed to save', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedRoleName = roles.find((r) => r.id === selectedRole)?.name || '';

  // Count how many accounts are allowed per operation
  const opCounts = {};
  OPERATIONS.forEach((op) => {
    opCounts[op.key] = (allPermissions[op.key] || []).length;
  });

  return (
    <div className="page">
      <PageHeader
        title="Account Permissions"
        subtitle="Configure which accounts each role can access for specific operations."
        crumbs={[{ label: 'Administration' }, { label: 'Account Permissions' }]}
      />

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-body">
          <div className="grid-3" style={{ alignItems: 'end' }}>
            <div className="form-group">
              <label className="form-label">Select Role</label>
              <SearchableSelect
                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                value={selectedRole || ''}
                onChange={(v) => setSelectedRole(Number(v))}
                placeholder="-- Select Role --"
              />
            </div>
          </div>
        </div>
      </div>

      {selectedRole && (
        <div className="card">
          <div className="card-body">
            {/* Operation tabs */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {OPERATIONS.map((op) => (
                <button
                  key={op.key}
                  className={`btn btn-sm ${activeOp === op.key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveOp(op.key)}
                >
                  {op.label}
                  {opCounts[op.key] > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.8 }}>
                      ({opCounts[op.key]})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Cascade info */}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-light, #f8f9fa)', padding: '8px 12px', borderRadius: 6, marginBottom: 16, lineHeight: 1.6 }}>
              <b>Cascade Rules:</b> Granting <b>View Accounts</b> auto-grants <b>Create/Edit/Delete/View Voucher</b> for those accounts. Revoking <b>View Accounts</b> auto-revokes them.
              Granting <b>View Reports</b> auto-grants <b>View Ledger</b>. Revoking <b>View Reports</b> auto-revokes it.
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {allowedIds.length} accounts selected for <b>{OPERATIONS.find((o) => o.key === activeOp)?.label}</b>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={expandAll}><FiRefreshCw /> Expand All</button>
                <button className="btn btn-secondary btn-sm" onClick={collapseAll}>Collapse All</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  <FiSave /> {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {loading ? (
              <Loader text="Loading accounts..." />
            ) : (
              <div style={{ maxHeight: 600, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                <AccountTree
                  nodes={accounts}
                  allowedIds={allowedIds}
                  onToggle={toggleAccount}
                  expanded={expanded}
                  onToggleExpand={toggleExpand}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!selectedRole && (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <h4>Select a Role</h4>
              <p>Choose a role above to configure account-level permissions.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
